// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

import { useCallback, useEffect, useRef, useState } from 'react';
import { calculationResultApi } from '../../../../api/calculationResultApi';
import type {
  CalculationResult,
  CalculationStatus,
  MaxFundingPayload,
} from '../../../../types/calculationResult';
import { migrateLegacyPayload } from './maxFundingDefaults';

export interface UseMaxFundingScenarioArgs {
  profileId: string;
  scenarioId: string | null;
  clientId?: string | null;
  initialPayload?: MaxFundingPayload;
}

export interface UseMaxFundingScenarioResult {
  scenario: CalculationResult | null;
  payload: MaxFundingPayload;
  isLoading: boolean;
  error: string | null;
  saving: boolean;
  isDirty: boolean;
  setPayload: (payload: MaxFundingPayload) => void;
  finalizeSave: (name: string) => Promise<CalculationResult>;
  reload: () => Promise<void>;
}

export function useMaxFundingScenario({
  profileId,
  scenarioId,
  clientId,
  initialPayload,
}: UseMaxFundingScenarioArgs): UseMaxFundingScenarioResult {
  const [scenario, setScenario] = useState<CalculationResult | null>(null);
  // The destructured setter is renamed because we expose a `setPayload`
  // wrapper below that also marks the form dirty — keeping the underlying
  // setter on a different identifier avoids the wrapper shadowing it.
  // SonarQube's S6754 flags the rename; this is intentional.
  const [payload, setPayloadState] = useState<MaxFundingPayload>(
    initialPayload ?? { scenarios: [] },
  );

  const [isDirty, setIsDirty] = useState<boolean>(scenarioId == null);
  const [isLoading, setIsLoading] = useState<boolean>(scenarioId != null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const hydratedRef = useRef(false);

  const reload = useCallback(async () => {
    if (!scenarioId) {
      // New in-memory scenario — nothing to fetch.
      hydratedRef.current = true;
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      hydratedRef.current = false;
      const fresh = await calculationResultApi.get(profileId, scenarioId);
      setScenario(fresh);
      setPayloadState(migrateLegacyPayload(fresh.payload));
      setIsDirty(false);
      hydratedRef.current = true;
    } catch (err) {
      const apiError = err as { response?: { data?: { message?: string } }; message?: string };
      setError(apiError.response?.data?.message || apiError.message || 'Failed to load scenario');
    } finally {
      setIsLoading(false);
    }
  }, [profileId, scenarioId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const setPayload = useCallback((next: MaxFundingPayload) => {
    setPayloadState(next);
    if (hydratedRef.current || scenario === null) {
      setIsDirty(true);
    }
  }, [scenario]);

  const finalizeSave = useCallback(
    async (name: string) => {
      const trimmed = name.trim();
      const updatedPayload: MaxFundingPayload = { ...payload, name: trimmed };
      try {
        setSaving(true);
        setError(null);

        let persisted: CalculationResult;
        if (scenario?.id) {
          // Existing row — PUT.
          persisted = await calculationResultApi.update(profileId, scenario.id, {
            reportType: scenario.reportType,
            clientId: scenario.clientId ?? undefined,
            status: 'SAVED' as CalculationStatus,
            payload: updatedPayload,
          });
        } else {
          // New in-memory scenario — POST.
          if (!clientId) {
            throw new Error('Cannot save a new scenario without a clientId');
          }
          persisted = await calculationResultApi.create(profileId, {
            reportType: 'MAXFUNDING',
            clientId,
            status: 'SAVED',
            payload: updatedPayload,
          });
        }

        setScenario(persisted);
        setPayloadState(persisted.payload);
        setIsDirty(false);
        return persisted;
      } catch (err) {
        const apiError = err as { response?: { data?: { message?: string } }; message?: string };
        setError(apiError.response?.data?.message || apiError.message || 'Save failed');
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [profileId, scenario, clientId, payload],
  );

  return { scenario, payload, isLoading, error, saving, isDirty, setPayload, finalizeSave, reload };
}
